import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  InputAdornment,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Stack,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as ActivateIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  AccountBalance as FinanceIcon,
  SupportAgent as SupportIcon,
  SmartToy as BotIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  LockClock as LockClockIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import userService from '../services/userService';
import configurationService from '../services/configurationService';
import UserForm from '../components/forms/UserForm';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

interface UsersPageState {
  users: User[];
  loading: boolean;
  error: string | null;
  page: number;
  rowsPerPage: number;
  total: number;
  searchTerm: string;
  selectedRole: string;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface MenuState {
  anchorEl: HTMLElement | null;
  user: User | null;
}

interface DialogState {
  confirmDelete: boolean;
  userForm: boolean;
  lockConfig: boolean;
  selectedUser: User | null;
}

const initialState: UsersPageState = {
  users: [],
  loading: false,
  error: null,
  page: 0,
  rowsPerPage: 10,
  total: 0,
  searchTerm: '',
  selectedRole: '',
  sortBy: 'nome',
  sortOrder: 'ASC',
};

const roleOptions = [
  { value: '', label: 'Todos os Perfis', icon: null },
  { value: 'administrador', label: 'Administrador', icon: <AdminIcon /> },
  { value: 'financeiro', label: 'Financeiro', icon: <FinanceIcon /> },
  { value: 'suporte', label: 'Suporte', icon: <SupportIcon /> },
  { value: 'bot', label: 'Bot', icon: <BotIcon /> },
];

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [state, setState] = useState<UsersPageState>(initialState);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [menu, setMenu] = useState<MenuState>({
    anchorEl: null,
    user: null,
  });
  const [dialogs, setDialogs] = useState<DialogState>({
    confirmDelete: false,
    userForm: false,
    lockConfig: false,
    selectedUser: null,
  });
  const [lockConfig, setLockConfig] = useState({
    minutes: 15,
    loading: false,
    saving: false,
  });

  // Carregar usuários
  const loadUsers = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setState(prev => ({ ...prev, loading: true, error: null }));
    }

    try {
      const params = {
        page: state.page + 1,
        limit: state.rowsPerPage,
        search: state.searchTerm || undefined,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
      };

      const response = await userService.getAllUsers(params);

      // Aplicar filtro de perfil no frontend (temporariamente)
      let users = response.users || [];
      if (state.selectedRole) {
        users = users.filter(user => user.perfil === state.selectedRole);
      }

      setState(prev => ({
        ...prev,
        users: users,
        total: response.pagination?.total || users.length,
        loading: false,
        error: null,
      }));

    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Erro ao carregar usuários',
        users: [],
        total: 0,
      }));
      showSnackbar('Erro ao carregar usuários', 'error');
    }
  }, [state.page, state.rowsPerPage, state.searchTerm, state.selectedRole, state.sortBy, state.sortOrder]);

  // Efeito para carregar usuários
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Mostrar snackbar
  const showSnackbar = (message: string, severity: SnackbarState['severity'] = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fechar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Abrir menu de ações
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setMenu({ anchorEl: event.currentTarget, user });
  };

  // Fechar menu de ações
  const handleCloseMenu = () => {
    setMenu({ anchorEl: null, user: null });
  };

  // Pesquisar usuários
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value, page: 0 }));
  };

  // Filtrar por perfil
  const handleRoleFilter = (event: any) => {
    setState(prev => ({ ...prev, selectedRole: event.target.value, page: 0 }));
  };

  // Mudar página
  const handlePageChange = (event: unknown, newPage: number) => {
    setState(prev => ({ ...prev, page: newPage }));
  };

  // Mudar linhas por página
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  // Callback para quando o usuário for criado/editado com sucesso
  const handleUserSuccess = () => {
    const action = dialogs.selectedUser ? 'atualizado' : 'criado';
    showSnackbar(`Usuário ${action} com sucesso`);
    setDialogs(prev => ({ ...prev, userForm: false, selectedUser: null }));
    loadUsers(false);
  };

  // Alternar status do usuário
  const handleOpenLockConfig = async () => {
    setDialogs(prev => ({ ...prev, lockConfig: true }));
    setLockConfig(prev => ({ ...prev, loading: true }));

    const minutes = await configurationService.getInactivityLockTimeoutMinutes();
    setLockConfig(prev => ({ ...prev, minutes, loading: false }));
  };

  const handleSaveLockConfig = async () => {
    const minutes = Number(lockConfig.minutes);

    if (!Number.isFinite(minutes) || minutes < 1 || minutes > 1440) {
      showSnackbar('Informe um tempo entre 1 e 1440 minutos', 'warning');
      return;
    }

    try {
      setLockConfig(prev => ({ ...prev, saving: true }));
      const savedMinutes = await configurationService.updateInactivityLockTimeoutMinutes(Math.floor(minutes));
      window.dispatchEvent(new CustomEvent('sysup:inactivity-timeout-updated', {
        detail: { minutes: savedMinutes },
      }));
      setLockConfig(prev => ({ ...prev, minutes: savedMinutes, saving: false }));
      setDialogs(prev => ({ ...prev, lockConfig: false }));
      showSnackbar('Tempo de bloqueio por inatividade salvo com sucesso');
    } catch (error: any) {
      setLockConfig(prev => ({ ...prev, saving: false }));
      showSnackbar(error.message || 'Erro ao salvar tempo de bloqueio', 'error');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await userService.toggleUserStatus(user.id);
      const action = user.status === 'ativo' ? 'bloqueado' : 'ativado';
      showSnackbar(`Usuário ${action} com sucesso`);
      handleCloseMenu();
      loadUsers(false);
    } catch (error: any) {
      showSnackbar(error.message || 'Erro ao alterar status do usuário', 'error');
    }
  };

  // Excluir usuário
  const handleDeleteUser = async () => {
    if (!dialogs.selectedUser) return;

    try {
      await userService.deleteUser(dialogs.selectedUser.id);
      showSnackbar('Usuário excluído com sucesso');
      setDialogs(prev => ({ ...prev, confirmDelete: false, selectedUser: null }));
      loadUsers(false);
    } catch (error: any) {
      showSnackbar(error.message || 'Erro ao excluir usuário', 'error');
    }
  };

  // Obter ícone do perfil
  const getRoleIcon = (role: string) => {
    const option = roleOptions.find(opt => opt.value === role);
    return option?.icon || <SupportIcon />;
  };

  // Obter cor do perfil
  const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (role) {
      case 'administrador': return 'primary';
      case 'financeiro': return 'secondary';
      case 'suporte': return 'warning';
      case 'bot': return 'default';
      default: return 'default';
    }
  };

  // Obter cor do status
  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    return status === 'ativo' ? 'success' : 'error';
  };

  const totalAtivos = state.users.filter((u) => u.status === 'ativo').length;
  const totalAdmins = state.users.filter((u) => u.perfil === 'administrador').length;

  return (
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'primary')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <PersonIcon sx={{ fontSize: 120, color: theme.palette.primary.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}` }}>
              <PersonIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gerenciamento de Usuários</Typography>
              <Typography variant="caption" color="text.secondary">Gerencie os usuários do sistema</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" size="small" startIcon={<LockClockIcon />} onClick={handleOpenLockConfig}>Configurar Bloqueio</Button>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialogs((prev) => ({ ...prev, userForm: true, selectedUser: null }))}>Novo Usuário</Button>
          </Stack>
        </Stack>
      </Paper>

      {!state.loading && state.total > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Total" valor={state.total} icone={<GroupsIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Ativos" valor={totalAtivos} subtitulo={`${state.total ? Math.round((totalAtivos / state.total) * 100) : 0}% do total`} icone={<ActivateIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Inativos" valor={state.total - totalAtivos} icone={<BlockIcon sx={{ color: theme.palette.error.main }} />} cor={theme.palette.error.main} bgCor={alpha(theme.palette.error.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Administradores" valor={totalAdmins} icone={<AdminIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={filtersPaperSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
          <FilterIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
          <Tooltip title="Atualizar"><IconButton size="small" onClick={() => loadUsers()} disabled={state.loading} sx={{ ml: 'auto' }}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField placeholder="Buscar por nome ou email..." value={state.searchTerm} onChange={handleSearch} fullWidth size="small" InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }} />
          <FormControl sx={{ minWidth: { sm: 200 } }} size="small">
            <InputLabel>Filtrar por Perfil</InputLabel>
            <Select value={state.selectedRole} onChange={handleRoleFilter} label="Filtrar por Perfil">
              {roleOptions.map((option) => (
                <SelectMenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{option.icon}{option.label}</Box>
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {state.error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: '0.8rem' }}>{state.error}</Alert>}

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Usuários</Typography>
          {!state.loading && <Chip size="small" label={`${state.total} usuário${state.total !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
        </Box>

        {state.loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando usuários...</Typography>
          </Box>
        ) : state.users.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Nenhum usuário encontrado</Typography>
            <Typography variant="caption" color="text.secondary">{state.searchTerm || state.selectedRole ? 'Ajuste os filtros aplicados' : 'Cadastre o primeiro usuário'}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={rankingTableHeadRowSx(theme)}>
                <TableCell>Avatar</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Perfil</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {state.users.map((user) => (
                  <TableRow key={user.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                    <TableCell sx={{ py: 0.5, px: 1.5, fontSize: '0.875rem' }}>
                      <Avatar
                        sx={{
                          bgcolor: getRoleColor(user.perfil) + '.main',
                          width: 32,
                          height: 32,
                        }}
                      >
                        {user.nome?.charAt(0).toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                        {user.nome}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5, fontSize: '0.875rem' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {user.usuario}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{user.email}</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5, fontSize: '0.875rem' }}>
                      <Chip
                        icon={getRoleIcon(user.perfil)}
                        label={user.perfil}
                        color={getRoleColor(user.perfil)}
                        variant="outlined"
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5, fontSize: '0.875rem' }}>
                      <Chip
                        label={user.status === 'ativo' ? 'Ativo' : 'Inativo'}
                        color={getStatusColor(user.status)}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{user.telefone || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                      {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5, px: 1.5 }}>
                      <IconButton
                        onClick={(e) => handleOpenMenu(e, user)}
                        size="small"
                        sx={{ p: 0.5 }}
                      >
                        <MoreVertIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {state.total > 0 && (
          <>
            <Divider />
            <Box sx={tablePaginationWrapperSx(theme)}>
              <TablePagination
                component="div"
                size="small"
                count={state.total}
                page={state.page}
                onPageChange={handlePageChange}
                rowsPerPage={state.rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Linhas:"
                sx={{ '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 }, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 } }}
              />
            </Box>
          </>
        )}
          </>
        )}
      </Paper>

      {/* Menu de ações */}
      <Menu
        anchorEl={menu.anchorEl}
        open={Boolean(menu.anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={() => {
            setDialogs(prev => ({ ...prev, userForm: true, selectedUser: menu.user }));
            handleCloseMenu();
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => menu.user && handleToggleStatus(menu.user)}
        >
          {menu.user?.status === 'ativo' ? (
            <>
              <BlockIcon sx={{ mr: 1 }} />
              Bloquear Usuário
            </>
          ) : (
            <>
              <ActivateIcon sx={{ mr: 1 }} />
              Ativar Usuário
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDialogs(prev => ({ ...prev, confirmDelete: true, selectedUser: menu.user }));
            handleCloseMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Excluir
        </MenuItem>
      </Menu>

      {/* Modal de formulário */}
      <UserForm
        open={dialogs.userForm}
        onClose={() => setDialogs(prev => ({ ...prev, userForm: false, selectedUser: null }))}
        user={dialogs.selectedUser}
        onSuccess={handleUserSuccess}
      />

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={dialogs.confirmDelete}
        onClose={() => setDialogs(prev => ({ ...prev, confirmDelete: false, selectedUser: null }))}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o usuário{' '}
            <strong>{dialogs.selectedUser?.nome}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogs(prev => ({ ...prev, confirmDelete: false, selectedUser: null }))}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogs.lockConfig}
        onClose={() => !lockConfig.saving && setDialogs(prev => ({ ...prev, lockConfig: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Configurar Tela Bloqueio</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Defina depois de quantos minutos sem atividade o sistema deve bloquear a tela.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Tempo de inatividade"
            value={lockConfig.minutes}
            disabled={lockConfig.loading || lockConfig.saving}
            onChange={(event) => setLockConfig(prev => ({
              ...prev,
              minutes: Number(event.target.value),
            }))}
            inputProps={{ min: 1, max: 1440, step: 1 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">minutos</InputAdornment>,
            }}
            helperText="Padrão do sistema: 15 minutos"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogs(prev => ({ ...prev, lockConfig: false }))}
            disabled={lockConfig.saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveLockConfig}
            variant="contained"
            disabled={lockConfig.loading || lockConfig.saving}
            startIcon={lockConfig.saving ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
